import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Linkedin, Mail, Phone, MapPin, ExternalLink } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { t } = useLanguage();
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { path: '/villa', label: t('nav.villa') },
    { path: '/bedrooms', label: t('nav.bedrooms') },
    { path: '/amenities', label: t('nav.amenities') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/booking', label: t('nav.booking') },
    { path: '/reviews', label: t('nav.reviews') },
  ];

  const legalLinks = [
    { path: '/policies', label: t('footer.terms') },
    { path: '/policies#cancellation', label: t('footer.cancellation') },
    { path: '/policies#privacy', label: t('footer.privacy') },
  ];

  const platformLinks = [
    { 
      name: 'Booking.com', 
      url: 'https://www.booking.com/hotel/gr/villa-aristotle-alepou.html' 
    },
    { 
      name: 'Airbnb', 
      url: 'https://www.airbnb.gr/rooms/1523140207073837497' 
    },
  ];

  return (
    <footer data-testid="main-footer" className="bg-[#1E3A5F] text-white">
      {/* Gold accent line */}
      <div className="h-1 bg-[#D4A72C]" />
      
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="font-serif text-xl font-semibold block mb-3">
              <span className="text-white">Villa </span>
              <span className="text-[#D4A72C]">Aristotle</span>
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-4">
              {t('footer.tagline')}
            </p>
            {/* Social Links - Instagram & LinkedIn only */}
            <div className="flex items-center gap-3 mb-6">
              <a
                href="https://www.instagram.com/villa.aristotle.cfu/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4A72C] transition-colors"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://www.linkedin.com/in/villa-aristotle-corfu-retreat-451208389"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-linkedin"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-[#D4A72C] transition-colors"
              >
                <Linkedin className="w-4 h-4" />
              </a>
            </div>

            {/* Platform Links */}
            <div>
              <p className="text-xs text-white/40 uppercase tracking-wider mb-2">
                {t('footer.alsoAvailableOn')}
              </p>
              <div className="flex flex-wrap gap-2">
                {platformLinks.map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-[#D4A72C] transition-colors"
                  >
                    {platform.name}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-sans font-semibold uppercase tracking-wider text-xs mb-4 text-[#D4A72C]">
              {t('footer.quickLinks')}
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-link-${link.path.replace('/', '')}`}
                    className="text-white/60 hover:text-[#D4A72C] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-sans font-semibold uppercase tracking-wider text-xs mb-4 text-[#D4A72C]">
              {t('footer.contact')}
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-[#D4A72C] mt-0.5 flex-shrink-0" />
                <a
                  href="https://maps.app.goo.gl/zpX7mdR27SFsptg38"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white/60 text-sm hover:text-[#D4A72C] transition-colors"
                >
                  {t('contact.addressStreet')}<br />
                  {t('contact.addressCity')}<br />
                  {t('contact.addressCountry')}
                </a>
              </li>
              <li>
                <a
                  href="tel:+306957020999"
                  data-testid="footer-phone-1"
                  className="flex items-center gap-2 text-white/60 hover:text-[#D4A72C] text-sm transition-colors"
                >
                  <Phone className="w-4 h-4 text-[#D4A72C]" />
                  +30 695 702 0999
                </a>
              </li>
              <li>
                <a
                  href="tel:+306937334121"
                  data-testid="footer-phone-2"
                  className="flex items-center gap-2 text-white/60 hover:text-[#D4A72C] text-sm transition-colors pl-6"
                >
                  +30 693 733 4121
                </a>
              </li>
              <li>
                <a
                  href="mailto:villa.aristotle.cfu@gmail.com"
                  data-testid="footer-email"
                  className="flex items-center gap-2 text-white/60 hover:text-[#D4A72C] text-sm transition-colors"
                >
                  <Mail className="w-4 h-4 text-[#D4A72C]" />
                  villa.aristotle.cfu@gmail.com
                </a>
              </li>
            </ul>
          </div>

          {/* Legal Links & Host */}
          <div>
            <h4 className="font-sans font-semibold uppercase tracking-wider text-xs mb-4 text-[#D4A72C]">
              {t('footer.legal')}
            </h4>
            <ul className="space-y-2 mb-6">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    data-testid={`footer-legal-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                    className="text-white/60 hover:text-[#D4A72C] text-sm transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            {/* License Badge */}
            <div className="p-3 bg-white/5 rounded-sm border border-[#D4A72C]/20">
              <p className="text-xs text-white/50">
                {t('footer.licenseText')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col md:flex-row items-center justify-center">
          <p className="text-white/40 text-xs">
            © {currentYear} Villa Aristotle. {t('footer.rights')} | Designed & Developed by <a href="#" style={{color:'inherit', opacity:0.6}}>Aionian Services</a>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
